import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CmsDocumentFields,
  Contabilita,
  DocumentCategory,
  Soggetto,
} from '../../anagraphics/models/cms-document-fields';
import { CmsKyc } from '../../anagraphics/models/cms-kyc';
import { Article } from '../../home/news/models/article';
import { CmsNews } from '../../home/news/models/cms-news';
import {
  CmsSurvey,
  CmsSurveyResponse,
} from '../../presentation-sheet/models/cms-survey';
import { QuestionarioQualitativo } from '../../presentation-sheet/models/presentation-sheet';
import {
  CmsFinanziamenti,
  CmsFinanziamento,
  Finanziamenti,
} from '../../requests/models/cms-finanziamenti';
import { Product, ProductType } from '../../requests/models/product';
import { Cities, CmsCities } from '../../_shared/models/cities';
import { CmsCounties, Counties } from '../../_shared/models/counties';
import { CmsTerritories } from '../../_shared/models/territories';
import { CmsField, ICmsField } from '../models/cms-field';
import { StoreData } from '../models/store-data';
import { CmsReliance } from '../../_shared/models/cms/cms-reliance';
import { StrapiFactory } from '../../_shared/models/cms/strapi-factory';
import {
  FlatStrapi,
  StrapiCollection,
  StrapiResponse,
} from '../../_shared/models/cms/strapi-response';
import { PraticaType, Target } from '../../../interfaces/pratica';
import { LoanService } from './loan.service';
import { CmsDocument } from '../../_shared/models/cms/cms-document';
import {
  FormField,
  FormFieldList,
  MimetypeValidator,
  SfSelectFormField,
  SfUtils,
} from 'ngx-smart-form';
import { UploadedDocument } from '../models/uploaded-document';
import { CerbixValidator } from '../../anagraphics/add-doc-anagraphic/cerbix-validator';

@Injectable({
  providedIn: 'root',
})
export class CmsService {
  private readonly endpoint = `${environment.api}/cms/api/v1`;
  private readonly cmsEndpoint = `${environment.cms}`;

  constructor(private httpClient: HttpClient) {}

  private _states = new BehaviorSubject<StoreData<CmsTerritories[]>>(
    new StoreData()
  );

  get state$() {
    if (this._states.value.state === 'init') {
      return this.getStates();
    } else {
      return this._states.asObservable().pipe(map((data) => data.data));
    }
  }

  getNews() {
    return this.httpClient.get<CmsNews[]>(`${this.endpoint}/news`).pipe(
      map((res) => {
        let mapped = res[0].data.articles.data.map(
          (article) => new Article(article)
        );
        return mapped;
      })
    );
  }

  getAffidamento() {
    return this.httpClient.get<[StrapiResponse<CmsReliance, 'reliance'>]>(
      `${this.endpoint}/affidamento`
    );
  }

  getSurvey(
    survey: QuestionarioQualitativo['data'] | undefined,
    productType: string,
    target: Target,
    soggetto: Soggetto
  ) {
    let params = new HttpParams()
      .set('type', productType)
      .set('target', target.toUpperCase())
      .set('soggetto', soggetto);
    return this.httpClient
      .get<[CmsSurveyResponse]>(`${this.endpoint}/questionari`, { params })
      .pipe(
        map((res) => {
          return CmsField.genFieldList(
            res[0].data.questions.data[0].attributes.question,
            survey
          );
        })
      );
  }

  getFinanziamenti(target: string) {
    const params = new HttpParams().set('target', target);
    return this.httpClient
      .get<CmsFinanziamenti[]>(`${this.endpoint}/finanziamenti`, {
        params,
      })
      .pipe(map((res) => Finanziamenti.getProductOptions(res[0])));
  }

  getFinanziamento(
    target: string,
    productType: string,
    selectedProduct?: Product
  ) {
    const params = new HttpParams()
      .set('target', target)
      .set('type', productType);
    return this.httpClient
      .get<CmsFinanziamento[]>(`${this.endpoint}/finanziamento`, {
        params,
      })
      .pipe(
        map((res) => {
          let max = res[0].data.financings.data[0]?.attributes.maxAmount;
          let min = res[0].data.financings.data[0]?.attributes.minAmount;
          let maxAmount = Number(max);
          let minAmount = Number(min);
          let sezioni = Finanziamenti.getFields(
            res[0]?.data.financings.data[0]?.attributes.sections || [],
            selectedProduct?.data
          );
          return {
            sezioni /* : [
              {
                title: 'Dati finanziamento',
                description: 'Inserisci i dati del finanziamento',
                fields: <FormFieldList>[
                  [
                    new SfSelectFormField({
                      formControlName: 'finalita',
                      options: [
                        { view: 'Liquidità', value: 'LIQUIDITA' },
                        { view: 'Investimento', value: 'INVESTIMENTO' },
                      ],
                    }),
                  ],
                ],
              },
            ] */,
            crif: res[0].data.financings.data[0]?.attributes.crif,
            maxAmount: !isNaN(maxAmount) ? maxAmount : 0,
            minAmount: !isNaN(minAmount) ? minAmount : 0,
            maxDuration: res[0].data.financings.data[0]?.attributes.maxDuration,
          };
        })
      );
  }

  getEstensioniFinanziamento(
    target: string,
    productType: string,
    selectedFinalita: string,
    selectedProduct?: Product
  ) {
    const params = new HttpParams()
      .set('target', target)
      .set('name', productType)
      .set('type', selectedFinalita);
    return this.httpClient
      .get(`${this.endpoint}/financing-extension`, {
        params,
      })
      .pipe(
        map((res) => {
          let max =
            res[0].data.financingExtensions.data[0]?.attributes.maxAmount;
          let min =
            res[0].data.financingExtensions.data[0]?.attributes.minAmount;
          let maxAmount = Number(max);
          let minAmount = Number(min);
          let sezioni = Finanziamenti.getFields(
            res[0]?.data.financingExtensions.data[0]?.attributes.sections || [],
            selectedProduct?.data
          );
          return {
            sezioni,
            crif: res[0].data.financingExtensions.data[0]?.attributes.crif,
            maxAmount: !isNaN(maxAmount) ? maxAmount : 0,
            minAmount: !isNaN(minAmount) ? minAmount : 0,
            maxDuration:
              res[0].data.financingExtensions.data[0]?.attributes.maxDuration,
          };
        })
      );
  }

  getExtension(praticaType: PraticaType, soggetto: Soggetto, area: string) {
    const params = new HttpParams()
      .set('tipoPratica', praticaType)
      .set('soggetto', soggetto)
      .set('area', area);
    return this.httpClient
      .get<CmsFinanziamento[]>(`${this.endpoint}/extensions`, { params })
      .pipe(
        map((res) => {
          let max = res[0].data.financings.data[0]?.attributes.maxAmount;
          let min = res[0].data.financings.data[0]?.attributes.minAmount;
          let maxAmount = Number(max);
          let minAmount = Number(min);
          return {
            sezioni: Finanziamenti.getFields(
              res[0]?.data.financings.data[0]?.attributes.sections || [],
              {}
            ),
            crif: res[0].data.financings.data[0]?.attributes.crif,
            maxAmount: !isNaN(maxAmount) ? maxAmount : 0,
            minAmount: !isNaN(minAmount) ? minAmount : 0,
            maxDuration: res[0].data.financings.data[0]?.attributes.maxDuration,
          };
        })
      );
  }

  getStates() {
    this._states.next(this._states.value.update('waiting'));
    return this.httpClient.get<CmsTerritories[]>(`${this.endpoint}/stati`).pipe(
      //map((res) => Territories.getOptions(res[0])),
      tap((res) =>
        this._states.next(this._states.value.update('completed', res))
      )
    );
  }

  getCounties() {
    return this.httpClient
      .get<CmsCounties[]>(`${this.endpoint}/province`)
      .pipe(map((res) => Counties.getOptions(res[0])));
  }

  getCities(provincia: string) {
    const params = new HttpParams().set('provincia', provincia);
    return this.httpClient
      .get<CmsCities[]>(`${this.endpoint}/municipalita`, { params })
      .pipe(map((res) => Cities.getOptions(res[0])));
  }

  getKyc(kyc?: Record<string, string>) {
    return this.httpClient.get<CmsKyc[]>(`${this.endpoint}/kyc`).pipe(
      map((res) => {
        return Finanziamenti.getFields(
          res[0]?.data.kyc.data.attributes.questions || [],
          kyc
        );
      })
    );
  }

  getDocFields(category: DocumentCategory) {
    let params = new HttpParams().set('categoria', category);
    return this.httpClient.get<
      [StrapiCollection<CmsDocumentFields, 'documentTypes'>]
    >(`${this.endpoint}/tipi-documento`, { params });
  }

  getDeplians(channelProducts: ProductType[]) {
    return this.httpClient
      .get<[StrapiResponse<CmsReliance, 'depliant'>]>(
        this.endpoint + '/fogliinformativi'
      )
      .pipe(
        map((res) => {
          let depliants = StrapiFactory.responseToView(res[0], 'depliant');
          return {
            ...depliants,
            products: depliants.products
              .map((product) => LoanService.mapProduct(product))
              .filter((product) => channelProducts.includes(product.type)),
          };
        })
      );
  }

  getTransparency() {
    return this.httpClient
      .get<[StrapiResponse<{ documents: CmsDocument[] }, 'transparency'>]>(
        this.endpoint + '/trasparenza'
      )
      .pipe(
        map((res) => {
          let transparencyResponse = StrapiFactory.responseToView(
            res[0],
            'transparency'
          );
          return StrapiFactory.flatStuff(transparencyResponse, [
            'documents',
            ['document'],
          ]) as unknown as {
            description: string;
            subtitle: string;
            title: string;
            documents: FlatStrapi<'document', CmsDocument>[];
          };
        })
      );
  }

  getLockedFlag() {
    return this.httpClient.get<StrapiResponse<{ locked: boolean }, 'lock'>[]>(
      this.endpoint + '/lock'
    );
  }

  getFile(path: string) {
    //let params = new HttpParams().set('path', path);
    return this.httpClient.get<Blob>(`${this.cmsEndpoint}${path}`, {
      responseType: 'blob' as any,
    });
  }
}
